import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { FilterTh } from '../common/column-filter-indicator'
/** Undeliverable addresses — the email-suppression list.
 *
 * Global read (every row is a per-ADDRESS fact, not a tenant's). The
 * recipient column is resolved live by the server through a digest
 * join against `users`; a row with no live match renders its short
 * digest, because the plaintext address is deliberately not stored.
 *
 * The lookup box is an EXACT-address lookup, not a search: substring
 * search over sha256 digests is structurally impossible, so the honest
 * control is "paste the address you are asking about".
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { queryKeys } from '../api/query-keys'
import {
  ClearFiltersButton,
  FilterField,
  FilteredEmptyState,
  ListPage,
  ListPagination,
  SearchField,
} from '../common/list-page'
import {
  ROW_LINK,
  ROW_LINK_ABOVE,
  TABLE,
  TABLE_HEAD_ROW,
  TABLE_ROW,
  TABLE_WRAP,
} from '../common/table'
import { adminEmailSuppressionsApi, messageForSuppressionError } from './api'
import { formatWhen, reasonProse, shortHash } from './format'
import type { SuppressionStatus } from './types'

const PAGE_SIZE = 50
const LOOKUP_DEBOUNCE_MS = 250

type LoadStatus = 'loading' | 'loaded' | 'error'

const STATUS_VALUES: readonly SuppressionStatus[] = ['active', 'released', 'all']

function parseStatus(raw: string | null): SuppressionStatus {
  return STATUS_VALUES.find((value) => value === raw) ?? 'active'
}

export function SuppressionsListPage(): JSX.Element {
  const [lookupInput, setLookupInput] = useState('')
  const [lookup, setLookup] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = parseStatus(searchParams.get('status'))
  const [page, setPage] = useState(0)

  useEffect(() => {
    const handle = setTimeout(
      () => setLookup(lookupInput.trim()),
      LOOKUP_DEBOUNCE_MS,
    )
    return () => clearTimeout(handle)
  }, [lookupInput])

  const params = {
    status: statusFilter,
    email: lookup || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }
  const listQuery = useQuery({
    queryKey: queryKeys.emailSuppressionList(params),
    queryFn: () => adminEmailSuppressionsApi.list(params),
    placeholderData: keepPreviousData,
  })
  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0
  const status: LoadStatus = listQuery.isPending
    ? 'loading'
    : listQuery.isError
      ? 'error'
      : 'loaded'
  const loadError =
    listQuery.error !== null ? messageForSuppressionError(listQuery.error) : null
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // Only the widened status views count as a rail filter; the lookup
  // stays visible, so it is excluded from the collapse-safety count.
  const hasFilters = lookup !== '' || statusFilter !== 'active'

  function setStatusFilter(next: SuppressionStatus): void {
    if (next === 'active') searchParams.delete('status')
    else searchParams.set('status', next)
    setSearchParams(searchParams, { replace: true })
    setPage(0)
  }

  function clearFilters(): void {
    setLookupInput('')
    setLookup('')
    setSearchParams(new URLSearchParams(), { replace: true })
    setPage(0)
  }

  return (
    <ListPage
      title="Undeliverable addresses"
      count={status === 'loaded' ? total : undefined}
      search={
        <SearchField
          id="suppression-lookup"
          label="Look up an address"
          placeholder="Paste the exact address to check…"
          value={lookupInput}
          onChange={(event) => {
            setLookupInput(event.target.value)
            setPage(0)
          }}
        />
      }
      activeFilterCount={statusFilter === 'active' ? 0 : 1}
      filterAction={
        <ClearFiltersButton hasFilters={hasFilters} onClear={clearFilters} />
      }
      filters={
        <>
          <FilterField active={statusFilter !== 'all'} label="Status" htmlFor="suppression-status-filter">
            <Select
              value={statusFilter}
              onValueChange={(next) => setStatusFilter(parseStatus(next))}
            >
              <SelectTrigger id="suppression-status-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="released">Released</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </>
      }
      pagination={
        status === 'loaded' && rows.length > 0 ? (
          <ListPagination
            summary={
              <>
                Showing {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </>
            }
            page={page}
            pageCount={pageCount}
            previousDisabled={page === 0}
            nextDisabled={page >= pageCount - 1}
            onPrevious={() => setPage((current) => Math.max(0, current - 1))}
            onNext={() =>
              setPage((current) => Math.min(pageCount - 1, current + 1))
            }
          />
        ) : undefined
      }
    >
      <>
        {status === 'loading' && (
          <p className="text-text-secondary">Loading undeliverable addresses…</p>
        )}
        {status === 'error' && (
          <div
            role="alert"
            className="bg-surface-card border border-danger/40 p-4 rounded-token-card"
          >
            <p className="font-medium">Could not load the undeliverable list</p>
            <p className="text-text-secondary text-body mt-1">{loadError}</p>
          </div>
        )}
        {status === 'loaded' && rows.length === 0 && (
          <FilteredEmptyState
            hasFilters={hasFilters}
            noun="undeliverable addresses"
            onClear={clearFilters}
            emptyHint="No undeliverable addresses — deliveries are healthy. When the bounce webhook reports a dead mailbox, it appears here."
          />
        )}
        {status === 'loaded' && rows.length > 0 && (
          <div
            className={
              listQuery.isPlaceholderData
                ? 'opacity-60 transition-opacity'
                : 'transition-opacity'
            }
          >
            <div className={TABLE_WRAP}>
              <table className={TABLE}>
                <thead>
                  <tr className={TABLE_HEAD_ROW}>
                    <th className="p-3 font-medium">Recipient</th>
                    <FilterTh className="p-3 font-medium" label="Email" filterDescription={lookup ? `Email lookup: ${lookup}` : undefined} />
                    <th className="p-3 font-medium">Client</th>
                    <th className="p-3 font-medium">Reason</th>
                    <th className="p-3 font-medium">Since</th>
                    <th className="p-3 font-medium">Last event</th>
                    <FilterTh className="p-3 font-medium" label="Status" filterDescription={statusFilter !== 'all' ? `${statusFilter === 'active' ? 'Active' : 'Released'} suppressions only` : undefined} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.emailHash} className={TABLE_ROW}>
                      <td className="p-3">
                        <Link
                          to={`/admin/email-suppressions/${row.emailHash}`}
                          className={ROW_LINK}
                        >
                          {row.user !== null ? (
                            row.user.name
                          ) : (
                            <span
                              className="font-mono text-text-secondary"
                              title={row.emailHash}
                            >
                              {shortHash(row.emailHash)}
                            </span>
                          )}
                        </Link>
                      </td>
                      <td className="p-3 text-text-secondary">
                        {row.user !== null ? row.user.email : '—'}
                      </td>
                      <td className="p-3 text-text-secondary">
                        {row.user !== null ? (
                          <Link
                            to={`/admin/clients/${row.user.clientId}`}
                            className={`underline ${ROW_LINK_ABOVE}`}
                          >
                            {row.user.clientName ?? row.user.clientId}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="p-3 text-text-secondary">
                        {reasonProse(row.reason)}
                      </td>
                      <td className="p-3 text-text-secondary">
                        {formatWhen(row.suppressedAt)}
                      </td>
                      <td className="p-3 text-text-secondary">
                        {formatWhen(row.lastEventAt)}
                      </td>
                      <td className="p-3 text-text-secondary">
                          {row.releasedAt === null
                            ? 'Active'
                            : `Released ${formatWhen(row.releasedAt)}`}
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    </ListPage>
  )
}
